import type { TFunction } from 'i18next'
import type { FieldValues, Path, UseFormSetError } from 'react-hook-form'
import type { z } from 'zod'
import type { ApiError } from './api'

/**
 * Translated messages for zod issues produced while validating forms with the shared contract
 * schemas. Passed per-parse to zodResolver so the same schema the API runs gives Arabic messages.
 */
export function zodErrorMap(t: TFunction<'common'>): z.core.$ZodErrorMap {
  return (issue) => {
    switch (issue.code) {
      case 'too_small':
        if (issue.origin === 'string') {
          return Number(issue.minimum) <= 1
            ? t('validation.required')
            : t('validation.minLength', { min: Number(issue.minimum) })
        }
        return t('validation.invalid')
      case 'too_big':
        if (issue.origin === 'string') return t('validation.maxLength', { max: Number(issue.maximum) })
        if (issue.origin === 'array') return t('validation.tooMany', { max: Number(issue.maximum) })
        return t('validation.invalid')
      case 'invalid_format':
        // An empty picker or field ('' fails z.uuid() as a format) is missing, not malformed.
        if (issue.input === undefined || issue.input === null || issue.input === '') {
          return t('validation.required')
        }
        return issue.format === 'email' ? t('validation.email') : t('validation.format')
      case 'invalid_type':
        return issue.input === undefined || issue.input === null || issue.input === ''
          ? t('validation.required')
          : t('validation.invalid')
      default:
        return t('validation.invalid')
    }
  }
}

/**
 * The contract schemas carry a few hand-written English messages (schema-level messages take
 * precedence over the error map). Translate the ones we know; anything else passes through.
 */
const CONTRACT_MESSAGES = {
  Required: 'validation.required',
  'Name is required': 'validation.required',
  'Enter a valid phone number': 'validation.phone',
  'Invalid amount': 'validation.format',
  'Use at least 10 characters': 'validation.passwordLength',
  'Password cannot start or end with a space': 'validation.passwordSpaces',
  'Choose a password different from the current one': 'validation.passwordSame',
  'Enter either a debit or a credit on each line': 'validation.journalLine',
  'An entry needs at least two lines': 'validation.journalTwoLines',
  'Describe the entry': 'validation.describe',
  'Describe the expense': 'validation.describe',
  'Enter an amount above zero': 'validation.positiveAmount',
  // Recurring expenses.
  'The end date cannot be before the start date': 'validation.endBeforeStart',
  'Choose an end date or a number of times, not both': 'validation.endOneWay',
  'No date falls before the end date': 'validation.noDateBeforeEnd',
  'The schedule must start on the expense’s date': 'validation.startOnExpense',
  'Give a reason': 'validation.reason',
  'Use letters, digits, dots or dashes': 'validation.accountCode',
  'Choose a different account to transfer to': 'validation.sameAccount',
  'Enter a percentage with up to 2 decimals': 'validation.percentage',
  'A rate cannot exceed 100%': 'validation.percentageMax',
  // Sales documents (contract and server messages).
  'Standard rates are above 0%; zero-rated, exempt and out-of-scope rates are 0%': 'validation.taxCategory',
  'Describe the line or choose a product': 'validation.describeLine',
  'Describe the line': 'validation.describeLine',
  'Quantity must be above zero': 'validation.positiveQuantity',
  'Price cannot be negative': 'validation.negativePrice',
  'Add at least one line': 'validation.atLeastOneLine',
  'The due date cannot be before the invoice date': 'validation.dueBeforeInvoice',
  'Due date cannot be before the invoice date': 'validation.dueBeforeInvoice',
  'Valid-until cannot be before the quotation date': 'validation.validUntilBefore',
  'Invalid quantity': 'validation.format',
  'Choose an active customer': 'validation.activeCustomer',
  'Choose an active product': 'validation.activeProduct',
  'Choose an active tax rate': 'validation.activeTaxRate',
  'Choose an active warehouse': 'validation.activeWarehouse',
  'This unit is counted in whole units': 'validation.wholeUnits',
  'A credit note cannot be dated before its invoice': 'validation.creditBeforeInvoice',
  'This customer is inactive': 'validation.inactiveCustomer',
  'This invoice belongs to another customer': 'validation.otherCustomerInvoice',
  'Only posted invoices can be credited': 'validation.invoiceNotCreditable',
  'Return this line in larger quantities: the amounts per unit are below one baisa':
    'validation.returnShareTooSmall',
  'Choose a tax rate the invoice charged': 'validation.invoiceTaxRate',
  'The invoice charged this rate at more than one percentage': 'validation.invoiceTaxRateAmbiguous',
  'Restore this product before taking it back': 'validation.restoreProductToReturn',
  'Debit notes charge services or amounts; invoice goods instead': 'validation.debitNoteGoods',
  // Catalog and inventory.
  'Add at least one value': 'validation.addValue',
  'A product needs at least one variant': 'validation.oneVariant',
  'Invalid cost': 'validation.cost',
  'Use letters, digits, dashes or underscores': 'validation.warehouseCode',
  // Purchasing and payables.
  'The due date cannot be before the purchase date': 'validation.dueBeforePurchase',
  'Choose an active supplier': 'validation.activeSupplier',
  'Goods are charged to inventory; leave the account empty': 'validation.goodsNoAccount',
  'Choose an active expense or asset account (not a cash, bank, inventory or receivables account)':
    'validation.expenseAccount',
  // Expenses and expense categories (the API's CHARGEABLE_ACCOUNT_MESSAGE).
  'Choose an active expense or asset account (not cash/bank or an account kept by documents: inventory, receivables, payables, cheques under collection or issued, contract advances or retention, VAT)':
    'validation.chargeableAccount',
  'Choose an active cash or bank account': 'validation.activeTreasury',
  'Cash goes through a cash account': 'validation.cashAccountOnly',
  'A cash account takes cash or cheques': 'validation.cashAccountMethods',
  'Choose an active account this method fits': 'validation.methodAccount',
  // A back-office document on a till's drawer (TILL_DRAWER_ACCOUNT, BR-POS-004).
  'This is a till drawer: choose another account': 'validation.tillDrawer',
  // The company's own payment methods (ADR-047).
  'Name the payment method': 'validation.required',
  'Cash at the till goes into the drawer': 'validation.cashAtTill',
  'A payment method with this name already exists': 'validation.paymentMethodTaken',
  'Documents were recorded with this method: its type cannot change': 'validation.paymentMethodTypeUsed',
  'This account does not take this type': 'validation.accountTakesType',
  'Choose an active payment method': 'validation.activePaymentMethod',
  'This method is not offered at the till': 'validation.notAtTill',
  'This purchase belongs to another supplier': 'validation.otherSupplierPurchase',
  'Only posted purchases can be settled': 'validation.purchaseNotPosted',
  'Only posted purchases can be returned or credited': 'validation.purchaseNotReturnable',
  'A supplier return cannot be dated before its purchase': 'validation.returnBeforePurchase',
  'Return each purchase line once': 'validation.returnLineOnce',
  'This line is not on the purchase': 'validation.lineNotOnPurchase',
  // Customer refunds.
  'Choose the credit to refund': 'validation.refundCredit',
  'A refund cannot be dated before the credit it pays out': 'validation.refundBeforeCredit',
  'This credit belongs to another customer': 'validation.otherCustomerCredit',
  'Credit not found': 'validation.creditNotFound',
  // A cheque's number (receipts, payments, refunds paid by cheque: ASM-254).
  'Enter the cheque number': 'validation.chequeNumber',
  // Point of sale.
  'Use up to 32 letters, digits, dashes or underscores': 'validation.tillCode',
  'Cannot be negative': 'validation.notNegative',
  'Not an accepted note or coin': 'validation.denomination',
  'Count whole notes and coins': 'validation.wholeCount',
  'Count the drawer before closing': 'validation.countDrawer',
  'Enter each note or coin once': 'validation.denominationOnce',
  'Enter a discount above zero': 'validation.positiveDiscount',
  'A percentage cannot exceed 100': 'validation.percentOver100',
  'Add at least one item': 'validation.atLeastOneItem',
  'Enter cash once': 'validation.cashOnce',
  'There is no cash tender': 'validation.noCashTender',
  'The cash handed over is less than the cash tender': 'validation.cashTenderedShort',
  'Discount cannot be negative': 'validation.negativeDiscount',
  'Choose the safe or bank, not the drawer': 'validation.notDrawer',
  'Till not found': 'validation.tillNotFound',
  'The discount is more than the sale': 'validation.discountOverSale',
  // Bank reconciliation (contract and server messages).
  'A line amount cannot be zero': 'validation.nonZeroAmount',
  'The period cannot end before it starts': 'validation.periodEnd',
  'The date is outside the statement period': 'validation.outsidePeriod',
  'Choose at least one statement line': 'validation.chooseStatementLine',
  'A line is listed twice': 'validation.listedTwice',
  'Describe the adjustment': 'validation.describe',
  'Choose a bank account': 'validation.bankAccount',
  'Cash accounts are counted, not reconciled: choose a bank, card or wallet account':
    'validation.notCashAccount',
  'A statement line is not on this statement': 'validation.lineNotOnStatement',
  'Choose an active account that can be posted to': 'validation.postableAccount',
  'Choose an income or expense account (not cash/bank, receivables, payables, inventory, VAT or customer credits)':
    'validation.adjustmentAccount',
} as const

export function translateMessage(t: TFunction<'common'>, message: string | undefined): string | undefined {
  if (!message) return undefined
  const key = CONTRACT_MESSAGES[message as keyof typeof CONTRACT_MESSAGES]
  return key ? t(key) : message
}

/**
 * Puts server-side field errors (problem `errors[]`) on the matching form fields. Returns the
 * issues that matched no field so the caller can show them in a form-level message.
 * `message` lets the caller translate per path/code (server messages are English).
 */
export function applyServerErrors<T extends FieldValues>(
  error: ApiError,
  setError: UseFormSetError<T>,
  isField: (path: string) => boolean,
  message: (issue: { path: string; message: string }) => string,
): { path: string; message: string }[] {
  const unmatched: { path: string; message: string }[] = []
  let first = true
  for (const issue of error.errors) {
    if (isField(issue.path)) {
      setError(issue.path as Path<T>, { type: 'server', message: message(issue) }, { shouldFocus: first })
      first = false
    } else {
      unmatched.push(issue)
    }
  }
  return unmatched
}
